/**
 * Classe Coppia timbrature
 * @param inizio, es 800 oppure 8.00
 * @param fine, es 1200 oppure 12.00
 * @constructor valorizza inizio e fine espressi come interi rappresentanti hhmm.
 * se fine < inizio (es. uscita nel giorno successivo rispetto alla timb. di entrata)
 * allora vengono valorizzati due intervalli: il primo fino a mezzanotte, il secondo da mezzanotte in poi...
 * Il costruttore iniziallizza infine un calcolatore (un istanza di Calc)
 */
import {CoppiaTimbrature} from "@commons/mapping/CoppiaTimbrature";

interface Parti {
    ore: number,
    frazione: number,
}

type BinaryPlus = 0 | 1 | 2;

export class Calc {
    static allFlat: BinaryPlus[] = [];
    flat: BinaryPlus[];

    constructor(intervallo?: number[] | Coppia) {

        if (Calc.allFlat.length === 0) {
            for (let indAllFlat = 0; indAllFlat < 1440; ++indAllFlat) {
                Calc.allFlat.push(1);
            }
        }

        this.flat = [];
        if (intervallo && Array.isArray(intervallo)) {
            for (let ind0 = 0; ind0 < 1440; ++ind0) {
                this.flat.push(intervallo[ind0] > 2 ? 2 : intervallo[ind0] as BinaryPlus);
            }
        } else if (typeof intervallo === 'object') {
            this.add(intervallo);
        }
    }

    static getParts = function (n: number | null) {
        if (n === null) {
            return null;
        }
        const nDec = 100;
        const intera = Math.trunc(n / nDec);
        const decimale = n - intera * nDec;
        return {ore: intera, frazione: decimale}; // N.B. Generalmente la frazione rappresenta i minuti!
    };

    static getMinutes = function (parts: Parti | null) {
        if (parts === null) {
            return 0;
        }
        return parts.ore * 60 + parts.frazione;
    };

    static parts2hhcc = function (parts: Parti | null) { // parts -> hhmm
        if (parts === null) {
            return 0;
        }
        const ore = parts.ore;
        let centesimi = Math.round(100 * parts.frazione / 60);
        centesimi /= 100.0;
        let number = ore;
        number += Number(centesimi);
        return number * 100.0;
    };

    static hhmm2hhcc = function (hhmm: number | null) {
        return Calc.parts2hhcc(Calc.getParts(hhmm));
    };

    static hhcc2hhmm = function (hhcc: number | null) {
        if (hhcc === null) {
            return 0;
        }
        const parts = Calc.getParts(hhcc) as Parti;
        const minuti = Math.round(60 * parts.frazione / 100);
        /*
          minuti /= 100.0;
          var number = parts.ore;
          number += Number(minuti);
          return Math.trunc(number * 100.0);*/
        const sOre = parts.ore.toString().padStart(2, '0');
        const sMinuti = minuti.toString().padStart(2, '0');
        return Number(sOre + sMinuti);
    };

    cancel() {
        this.flat = [];
    };

    add(param: BinaryPlus[] | Coppia) {
        const firstTimeAdd = this.flat.length === 0;
        if (Array.isArray(param)) {
            for (let ind0 = 0; ind0 < 1440; ++ind0) {
                if (firstTimeAdd) {
                    this.flat.push(0);
                }
                this.flat[ind0] += param[ind0];
            }
        } else {
            if (typeof param === 'object') {
                const copia = param;
                const inizio = Calc.getMinutes(Calc.getParts(copia.inizio));
                const fine = Calc.getMinutes(Calc.getParts(copia.fine));
                const inizio2 = Calc.getMinutes(Calc.getParts(copia.inizio2));
                const fine2 = Calc.getMinutes(Calc.getParts(copia.fine2));
                for (let ind0 = 0; ind0 < 1440; ++ind0) {
                    if (firstTimeAdd) {
                        this.flat.push(0);
                    }
                    if (inizio !== null) {
                        if (ind0 >= inizio && ind0 < fine) {
                            this.flat[ind0] = 1;
                        }
                    }
                    if (inizio2 !== null) {
                        if (ind0 >= inizio2 && ind0 < fine2) {
                            this.flat[ind0] = (this.flat[ind0] + 1) as BinaryPlus;
                        }
                    }
                }
            } else if (!firstTimeAdd) {
                let partenza = -1;
                for (let ind0 = 1440 - 1; ind0 >= 0; --ind0) {
                    if (this.flat[ind0] > 0) {
                        partenza = ind0 + 1;
                        break;
                    }
                }
                if (partenza >= 0) {
                    for (let ind0 = partenza; param > 0 && ind0 < 1440; ++ind0) {
                        this.flat[ind0] = (this.flat[ind0] + 1) as BinaryPlus;
                        --param;
                    }
                }
            }
        }
        return this.flat;
    };

    addCoppie(coppie: Coppia[]) {
        for (let indE = 0; indE < coppie.length; ++indE) {
            this.add(coppie[indE].calc.getFlat());
        }
    }

    invert() {
        const newCalc = new Calc();
        const firstTimeAdd = newCalc.flat.length === 0;
        for (let ind0 = 0; ind0 < 1440; ++ind0) {
            if (firstTimeAdd) {
                newCalc.flat.push(0);
            }
            newCalc.flat[ind0] = this.flat[ind0] > 0 ? 0 : 1;
        }

        return newCalc;
    };

    getCount(c?: Calc, c2?: Calc, c3?: Calc) {
        let count = 0;
        const filter = c ? c.flat : Calc.allFlat;
        const filter2 = c2 ? c2.flat : Calc.allFlat;
        const filter3 = c3 ? c3.flat : Calc.allFlat;
        for (let ind0 = 0; ind0 < 1440; ++ind0) {
            if ((this.flat[ind0] > 0) && filter[ind0] && filter2[ind0] && filter3[ind0]) {
                count += this.flat[ind0];
            }
        }
        return count;
    };

    getSingleCount(c?: Calc, c2?: Calc, c3?: Calc) {
        let count = 0;
        const filter = c ? c.flat : Calc.allFlat;
        const filter2 = c2 ? c2.flat : Calc.allFlat;
        const filter3 = c3 ? c3.flat : Calc.allFlat;
        for (let ind0 = 0; ind0 < 1440; ++ind0) {
            if ((this.flat[ind0] > 0) && filter[ind0] && filter2[ind0] && filter3[ind0]) {
                count += (this.flat[ind0] > 0 ? 1 : 0);
            }
        }
        return count;
    };

    getFlat(c?: Calc, c2?: Calc, c3?: Calc) {
        const newFlat: BinaryPlus[] = [];
        const filter = c ? c.flat : Calc.allFlat;
        const filter2 = c2 ? c2.flat : Calc.allFlat;
        const filter3 = c3 ? c3.flat : Calc.allFlat;
        for (let ind0 = 0; ind0 < 1440; ++ind0) {
            const minuto = ((this.flat[ind0] > 0) && filter[ind0] && filter2[ind0] && filter3[ind0]) ? this.flat[ind0] : 0;
            newFlat.push(minuto);
        }
        return newFlat;
    };

    getFlatEcc(c?: Calc, c2?: Calc, c3?: Calc) {
        const newFlat: BinaryPlus[] = [];
        const filter = c ? c.flat : Calc.allFlat;
        const filter2 = c2 ? c2.flat : Calc.allFlat;
        const filter3 = c3 ? c3.flat : Calc.allFlat;
        for (let ind0 = 0; ind0 < 1440; ++ind0) {
            const minuto = ((this.flat[ind0] > 0) && filter[ind0] && filter2[ind0] && filter3[ind0]) ? this.flat[ind0] : 0;
            if (minuto === 0 && this.flat[ind0] > 1) {
                newFlat.push(1);
            } else {
                newFlat.push(minuto);
            }
        }
        return newFlat;
    };

    getIntervalli(): CoppiaTimbrature[] {
        const coppie: Coppia[] = [];
        let indexInizio: number | null = null;
        let indexFine: number | null = null;

        for (let ind0 = 0; ind0 < 1440; ++ind0) {
            if (this.flat[ind0] > 0 && indexInizio !== null) {
                indexFine = ind0 + 1;
            }
            if (this.flat[ind0] > 0 && indexInizio === null) {
                indexInizio = ind0;
            }
            if (this.flat[ind0] === 0 && indexInizio !== null && indexFine !== null) {
                const inizio = Math.trunc(indexInizio / 60) * 100 + (indexInizio % 60);
                const fine = Math.trunc((indexFine) / 60) * 100 + (indexFine % 60);
                const coppia = new Coppia(inizio, fine);
                indexInizio = null;
                indexFine = null;
                coppie.push(coppia);
            }
        }
        if (indexInizio !== null) { //  Presente fino a mezzanotte!
            const inizio = Math.trunc(indexInizio / 60) * 100 + (indexInizio % 60);
            const fine = 2400;
            const coppia = new Coppia(inizio, fine);
            coppie.push(coppia);
        }

        const coppieTimbrature: CoppiaTimbrature[] = [];
        for (const coppia of coppie)
            coppieTimbrature.push(coppia.getCoppiaTimbrature());
        return coppieTimbrature;
    };

}

function isInt(n: any) {
    return Number(n) === n && n % 1 === 0;
}

function isFloat(n: any) {
    return Number(n) === n && n % 1 !== 0;
}

export class Coppia {
    daElaborare: boolean;
    elaborata: boolean;
    inizio: number | null;
    fine: number | null;
    inizio2: number | null;
    fine2: number | null;
    calc: Calc;

    constructor(a: number | CoppiaTimbrature, b?: number) {

        this.daElaborare = false; // Flag generico utile a identificare se la copia deve essere elaborata o meno, esempio nel computo degli straordinari
        this.elaborata = false; // Flag generico utile a identificare se la copia è gia' stata elaborata o meno, esempio nel computo degli straordinari
        this.inizio = null;
        this.fine = null;
        this.inizio2 = null;
        this.fine2 = null;

        if (!Math.trunc) {
            Math.trunc = function (v) {
                v = +v;
                return (v - v % 1) || (!isFinite(v) || v === 0 ? v : v < 0 ? -0 : 0);
            };
        }

        if (b || b === 0) { // i 2 parametri sono 2 numeri
            this.calc = this.constructorByInizioFine(a as number, b);
        } else {
            if (a || a === 0) { // un solo parametro di tipo CoppiaTimbrature
                this.calc = this.constructorByInizioFine(CoppiaTimbrature.getEntrata(a as CoppiaTimbrature)!, CoppiaTimbrature.getUscita(a as CoppiaTimbrature)!);
            } else {
                throw new Error('Parametri errati!');
            }
        }
    }

    getInizio() {
        return this.inizio;
    }

    getFine() {
        return this.fine2 !== null ? this.fine2 : this.fine;
    }

    getCoppiaTimbrature(): CoppiaTimbrature {
        let entrata = '0000' + this.getInizio();
        entrata = entrata.slice(-4);
        let uscita = '0000' + this.getFine();
        uscita = uscita.slice(-4);
        return CoppiaTimbrature.factory({entrata, uscita});
    }

    private constructorByInizioFine = (inizio: number, fine: number) => {

        this.inizio = null;
        this.fine = null;
        if (inizio !== null && fine !== null) {
            this.inizio = isInt(inizio) ? inizio : Math.trunc(inizio * 100);
            if (this.inizio !== 0 && fine === 0) // Mezzanotte!!!
                this.fine = 2400;
            else
                this.fine = isInt(fine) ? fine : Math.trunc(fine * 100);
        }
        this.inizio2 = null;
        this.fine2 = null;
        if (this.inizio && this.fine && (this.fine < this.inizio)) {
            this.inizio2 = 0;
            this.fine2 = this.fine;
            this.fine = 2400;
        }
        return new Calc(this);
    };
}

/*
Es. Utilizzo:

const copia = new Coppia(8.30, 1200);
const c = new Calc(copia);

	    console.log(c.getCount() / 60);
	    copia = new Coppia(1330, 18.005);
	    c.add(copia);
	    console.log(c.getCount() / 60);

 */

/*<any>
    <p>This is the partial for view 1.</p>
    <label>inizio</label>
    <input type="text" name="inizio" ng-model="inizio">
    <label>fine</label>
    <input type="text" name="fine" ng-model="fine">

    <label>inizio2</label>
    <input type="text" name="inizio2" ng-model="inizio2">
    <label>fine2</label>
    <input type="text" name="fine2" ng-model="fine2">

    <button ng-click="calcola()">calcola</button>
    <button ng-click="reset()">reset</button>
    totale {{(calc.getCount()+ calc2.getCount())/ 60}}<br>
    giorno {{calc.getCount(cGiorno) / 60}}<br>
    notte {{static getgetCount(cNotte) / 60}}
</any>*/

function engineTestCtrl(inizio: number, fine: number, inizio2: number, fine2: number) {
    return {
        cNotte: new Coppia(2200, 600).calc,
        cGiorno: new Coppia(600, 2200).calc,
        c: new Coppia(8.30, 1200).calc,

        calcola: function () {
            const copia = new Coppia(Number(inizio), Number(fine));
            const copia2 = new Coppia(Number(inizio2), Number(fine2));
            return {c1: copia.calc, c2: copia2.calc};
        },

        reset: function (calc: Calc) {
            calc.cancel();
        },
    }
}
